import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { GridApi } from 'ag-grid-community';

import SecondaryMenu from '../menu/secondaryMenu.component';
import Button from '../shared/button.component';
import ConfirmDialog from '../shared/confirmDialog.component';
import NavigationPrompt from '../shared/navigationPrompt.component';
import { modalDataSelector } from '../shared/modals/modals.selectors';
import { withPermissions, permissions } from '../shared/authorization';
import { UserWithActiveFlag, useUsersExcelData } from '../shared/queries/users';
import { usePrevious } from '../shared/hooks/usePrevious';
import { currentPeriodSelector, environmentSuffixSelector } from '../shared/store/selectors';
import { ConfirmModalData } from '../shared/types';
import { errorNotification } from '../shared/store/notificationActions';
import {
  useImportUsers,
  UserWithValidationData,
  useUploadUsers,
} from '../shared/mutations/users';

import {
  fetchInitialDataForUserSettingsPage,
  fetchExpirationDatePrivilege,
  showUserSettingsFormForAdd,
  showUserSettingsFormForEdit,
  showConfirmDialogModal,
  hideConfirmDialogModal,
  setUserModified,
  resetUserToEdit,
} from './store/actions';
import {
  isUserModifiedSelector,
  userToEditSsoSelector,
  isUserReadOnlySelector,
  rolesContextSelector,
  isAllowedToEditUsersSelector,
  isLoadingUsersSelector,
  isSavingInProgressSelector,
  isUserExpirationDateSelector,
} from './store/selectors';

import UsersGrid from './usersGrid.component';
import AddEditUserSettingsForm from './addEditUserSettingsForm.component';
import HamburgerDropdown from './hamburgerDropdown.component';
import { ConfirmDialogModal } from './constants';
import downloadXlsx from './downloadXlsx';
import { AppState } from '../stateType';
import { handleUsersUpload } from './xlsxImport/handleUsersUpload';

import Config from '../config';
import { TabTypes } from '../administration/shared/constants';
import AdminEntityImport from '../administration/shared/adminEntityImport.component';
import { importColumnDefinitions } from './importColumnDefinitions';
import { ViewImportTabs } from '../administration/shared/ViewImportTabs.component';
import { userRolesDictionary } from '../shared/authorization/userRoles';

type CurrentPeriod = {
  name: string,
};

export type RolesContext = Map<string, {
  clientId: number,
  roles: Map<string, number>
}>;

type UserSettingsPageParams = {
  fetchInitialDataForUserSettingsPage:
    (isFetchingInitialDataForUserSettings: boolean, currentPeriod: CurrentPeriod) => Promise<void>,
  fetchExpirationDatePrivilege: () => Promise<void>,
  showUserSettingsFormForAdd: Function,
  showUserSettingsFormForEdit: (selectedSso: string, readOnly: boolean) => void,
  resetUserToEdit: Function,
  users: UserWithActiveFlag[],
  isLoadingUsers: boolean,
  isSavingInProgress: boolean,
  userToEditSso?: string,
  openFormForAdd?: boolean,
  openFormForEdit?: boolean,
  isFetchingInitialDataForUserSettings: boolean,
  confirmDialogData?: ConfirmModalData,
  showConfirmDialogModal: Function,
  hideConfirmDialogModal: Function,
  setUserModified: Function,
  isUserModified: boolean,
  readOnly: boolean,
  currentPeriod: {
    name: string,
  },
  loggedInUserSso: string,
  canEditHimself: boolean,
  canModify: boolean,
  environmentSuffix: string,
  rolesContext: RolesContext,
  isAllowedToEditUsers: boolean,
  isUserExpirationDate: boolean,
}

// eslint-disable-next-line no-empty-function
const noop = () => {};

const UserSettingsPage = ({
  fetchInitialDataForUserSettingsPage,
  fetchExpirationDatePrivilege,
  showUserSettingsFormForAdd,
  showUserSettingsFormForEdit,
  users,
  isLoadingUsers,
  isSavingInProgress,
  userToEditSso,
  openFormForAdd,
  openFormForEdit,
  isFetchingInitialDataForUserSettings,
  confirmDialogData,
  showConfirmDialogModal,
  hideConfirmDialogModal,
  setUserModified,
  isUserModified,
  readOnly,
  currentPeriod,
  loggedInUserSso,
  canEditHimself,
  canModify,
  environmentSuffix,
  isAllowedToEditUsers,
  isUserExpirationDate,
}: UserSettingsPageParams) => {
  const { refetch: refetchExcelUserData, isLoading: isUsersExcelDataLoading } = useUsersExcelData();

  const [usersGridApi, setUsersGridApi] = useState<GridApi | null>(null);
  const [uploadedUsers, setUploadedUsers] = useState<UserWithValidationData[]>([]);

  const prevUserToEditSso = usePrevious(userToEditSso);

  const [activeTab, setActiveTab] = useState(TabTypes.VIEW);

  const showUserSettingsFormForView = useCallback(selectedSso => {
    if (!selectedSso) {
      return;
    }

    const readOnly = true;
    showUserSettingsFormForEdit(selectedSso, readOnly);
  }, [showUserSettingsFormForEdit]);

  const refreshUsersPage = useCallback(() => {
    setUploadedUsers([]);
    setActiveTab(TabTypes.VIEW);
    fetchInitialDataForUserSettingsPage(isFetchingInitialDataForUserSettings, currentPeriod).then(() => {
      usersGridApi?.deselectAll();
    });
    fetchExpirationDatePrivilege();
  }, [
    currentPeriod,
    fetchInitialDataForUserSettingsPage,
    fetchExpirationDatePrivilege,
    isFetchingInitialDataForUserSettings,
    usersGridApi]);

  const { mutate: importUsersApi, isLoading: isImportingUsers } = useImportUsers(refreshUsersPage);
  const { mutate: uploadUsersApi, isLoading: isUploadingUsers } = useUploadUsers(setUploadedUsers);

  const importUsers = useCallback(() => {
    const ssoWithErrors = new Set(uploadedUsers.filter(user => user.validationData).map(({ sso }) => sso));

    const usersToImport = uploadedUsers
      .filter(({ sso }) => !ssoWithErrors.has(sso))
      .map(({ validationData, ...user }) => user);

    importUsersApi(usersToImport);
  }, [importUsersApi, uploadedUsers]);

  const selectCurrentUser = useCallback(() => {
    usersGridApi?.forEachNode(node => {
      if (node.data.sso === userToEditSso) {
        node.setSelected(true);
        usersGridApi.ensureNodeVisible(node);
      }
    });
  }, [userToEditSso, usersGridApi]);

  useEffect(() => {
    refreshUsersPage();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (usersGridApi) {
      if (userToEditSso && userToEditSso !== prevUserToEditSso ) {
        return selectCurrentUser();
      }

      const selectedRows = usersGridApi.getSelectedRows();
      if (selectedRows.length && selectedRows[0].sso !== userToEditSso) {
        return selectCurrentUser();
      }
    }
  }, [prevUserToEditSso, selectCurrentUser, userToEditSso, usersGridApi]);

  const showUserSettingsFormForAddCallback = useCallback(() => {
    usersGridApi?.deselectAll();
    showUserSettingsFormForAdd();
  }, [showUserSettingsFormForAdd, usersGridApi]);

  const onUsersGridReady = useCallback(params => {
    setUsersGridApi(params.api);
    params.api.sizeColumnsToFit();
  }, []);

  const deselectUser = useCallback(() => {
    usersGridApi?.deselectAll();
  }, [usersGridApi]);

  const fileToImportChange = useCallback(async (file: File) => {
    try {
      await handleUsersUpload({
        file,
        uploadUsersApi,
      });
    } catch (er) {
      errorNotification('Fetching users failed');
    }
  }, [uploadUsersApi]);

  const handleHamburgerDropdownFileToImportChange = useCallback((prop: React.ChangeEvent<HTMLInputElement>) => {
    const file = prop.target?.files?.[0];
    if (!file) {
      return;
    }
    return fileToImportChange(file);
  }, [fileToImportChange]);


  const fileToImportSelect = useCallback(hideDropdown => () => {
    hideDropdown();
    setActiveTab(TabTypes.IMPORT);
  }, []);

  const exportToExcel = useCallback(async callback => {
    const { data: users, isSuccess } = await refetchExcelUserData();
    if (isSuccess && users) {
      try {
        await downloadXlsx({
          users: users.map(
            ({ roleName, ...rest }) => ({ ...rest, roleName: userRolesDictionary[roleName] || roleName })
          ),
          environmentSuffix,
        });
      } catch (err) {
        errorNotification(err);
      }
    }
    callback?.();
  }, [environmentSuffix, refetchExcelUserData]);

  const idFieldComparator = useCallback((entity: any, importedEntity: any) => (
    (entity.sso === importedEntity.sso) && entity.clients.split(',')
      .map((el: string) => el.trim()).includes(importedEntity.clientName)
  ), []);

  const templateFileName = `TLS_Users_And_Privileges_Template${environmentSuffix}.xlsx`;

  const isGridLoading = isFetchingInitialDataForUserSettings || isLoadingUsers || isSavingInProgress;

  const renderViewTab = () => (
    <>
      <div className="row no-gutters">
        <div className="col">
          <UsersGrid
            componentToRenderOnSearchBar={(
              <Button
                className="a-my-10"
                kind="primary"
                type="button"
                disabled={openFormForAdd || isGridLoading}
                onClick={showUserSettingsFormForAddCallback}
              >
                Add User
              </Button>
            )}
            users={users}
            showUserSettingsFormForView={showUserSettingsFormForView}
            showUserSettingsFormForEdit={showUserSettingsFormForEdit}
            onUsersGridReady={onUsersGridReady}
            addingNewUser={openFormForAdd}
            isUserModified={isUserModified}
            currentlySelectedSso={userToEditSso}
            isGridLoading={isGridLoading}
            confirmDialogData={confirmDialogData}
            showConfirmDialogModal={showConfirmDialogModal}
            hideConfirmDialogModal={hideConfirmDialogModal}
            openFormForEdit={openFormForEdit}
            readOnly={readOnly}
            canEditHimself={canModify && canEditHimself}
            canEditUsers={canModify}
            canAddUser={canModify}
            loggedInUserSso={loggedInUserSso}
          />
        </div>
      </div>
      <div className="row no-gutters">
        <div className="col">
          {openFormForAdd ? (
            <AddEditUserSettingsForm
              deselectUser={deselectUser}
              showUserSettingsFormForView={showUserSettingsFormForView}
              setUserModified={setUserModified}
              isUserModified={isUserModified}
              canModify={canModify}
              usersGridApi={usersGridApi}
              canEditExpirationDate={isUserExpirationDate}
            />
          ) : null}
          {openFormForEdit ? (
            <AddEditUserSettingsForm
              userToEditSso={userToEditSso}
              openForEdit
              deselectUser={deselectUser}
              setUserModified={setUserModified}
              isUserModified={isUserModified}
              showUserSettingsFormForView={showUserSettingsFormForView}
              canModify={canModify}
              usersGridApi={usersGridApi}
              canEditExpirationDate={isUserExpirationDate}
            />) : null}
        </div>
      </div>
    </>
  );

  const renderImportTab = () => (
    <>
      <AdminEntityImport
        columnDefs={importColumnDefinitions as any}
        entities={users}
        uploadedEntities={uploadedUsers}
        uploadingEntities={isUploadingUsers || isImportingUsers}
        selectFileToUpload={fileToImportChange}
        importEntities={importUsers}
        templateFileName={templateFileName}
        templatePath={Config.templates.USERS_AND_PRIVILEGES}
        pageTitle={'Users and privileges'}
        idField={'sso'}
        idFieldComparator={idFieldComparator}
        promptOnExit
      />
    </>
  );

  return (
    <div className="user-settings-page">
      <NavigationPrompt show={isUserModified}/>
      <div className="ag-grid-full-page-with-secondary-menu admin-import-page">
        <SecondaryMenu>
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown
              canModify={canModify && isAllowedToEditUsers}
              disableEditing={isUsersExcelDataLoading || isImportingUsers}
              exportToExcel={exportToExcel}
              templateFileName={templateFileName}
              templatePath={Config.templates.USERS_AND_PRIVILEGES}
              fileToImportChange={handleHamburgerDropdownFileToImportChange}
              fileToImportSelect={fileToImportSelect}
            />
          </div>
        </SecondaryMenu>
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>Users and Privileges</h5>
              </div>
            </div>
          </div>
          <ViewImportTabs
            activeTab={activeTab}
            changeTab={setActiveTab}
            renderViewTab={renderViewTab} // eslint-disable-line react/jsx-no-bind
            renderImportTab={renderImportTab} // eslint-disable-line react/jsx-no-bind
            viewTabOnly={false}
            canModify
          />
        </div>
      </div>
      <ConfirmDialog
        onConfirm={confirmDialogData?.data?.onConfirm || noop}
        onCancel={hideConfirmDialogModal}
        isVisible={confirmDialogData?.show}
      />
    </div>
  );
};

function mapStateToProps({ users, ...state }: AppState) {
  return {
    users: users.users,
    isLoadingUsers: isLoadingUsersSelector({ users }),
    isSavingInProgress: isSavingInProgressSelector({ users }),
    isFetchingInitialDataForUserSettings: users.isFetchingInitialDataForUserSettings,
    environmentSuffix: environmentSuffixSelector({ ...state }),
    userToEditSso: userToEditSsoSelector({ users }),
    openFormForAdd: users.openFormForAdd,
    openFormForEdit: users.openFormForEdit,
    confirmDialogData: modalDataSelector(state, ConfirmDialogModal),
    isUserModified: isUserModifiedSelector({ users }),
    readOnly: isUserReadOnlySelector({ users }),
    currentPeriod: currentPeriodSelector(state),
    canEditHimself: state.user.user.permissions.commands.canEditHimself,
    loggedInUserSso: state.user.user.sso,
    rolesContext: rolesContextSelector({ users }),
    isAllowedToEditUsers: isAllowedToEditUsersSelector({ users }),
    isUserExpirationDate: isUserExpirationDateSelector({ users }),
  };
}

export default withPermissions(permissions.USER_ADMINISTRATION)(connect(
  mapStateToProps,
  {
    fetchInitialDataForUserSettingsPage,
    fetchExpirationDatePrivilege,
    showUserSettingsFormForAdd,
    showUserSettingsFormForEdit,
    showConfirmDialogModal,
    hideConfirmDialogModal,
    setUserModified,
    resetUserToEdit,
  }
)(UserSettingsPage));
